import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

import { BootOverlay, HudNav, MobileBottomNav } from "../Bootoverlay/Bootoverlay";
import PanelHero from "../Panelhero/Panelhero";
import PanelWorld from "../Panelworld/Panelworld";
import PanelKart from "../Panelkart/Panelkart";
import PanelDriver from "../Paneldriver/Paneldriver";
import PanelTours from "../Paneltours/Paneltours";
import PanelMissions from "../Panelmissions/Panelmissions";
import PanelInfo from "../PanelInfo/Panelinfo";
import MissionModal from "../Paneltours/Toursmodal";
import AppPromoPopup from "../Panelhero/Apppromopopup";
import PanelCorporate from "../PanelEvents/PanelEvents";
import { selectModalOpen } from "../Paneltours/PanelTourSlice/PanelTourSlice";

import "../styles/Base.css";
import "../styles/Boot.css";
import "../styles/Hero.css";
import "../styles/World.css";
import "../styles/Kart.css";
import "../styles/Driver.css";
import "../styles/Mission.css";
import "../styles/Trophy.css";
import "../styles/Tours.css";
import "../styles/Info.css";
import "../styles/Events.css";

const SEED_BROADCASTS = 847;
const SEED_DRIVERS = 312;

const PANEL_IDS = [
  "panel-hero",
  "panel-world",
  "panel-kart",
  "panel-tours",
  "panel-driver",
  "panel-missions",
  "panel-info",
  "panel-event",
];

const PANEL_ROUTES = [
  "/",
  "/about",
  "/kart",
  "/tours",
  "/driver",
  "/missions",
  "/contact",
  "/event",
];

function pathToPanel(pathname) {
  const idx = PANEL_ROUTES.indexOf(pathname);
  return idx >= 0 ? idx : 0;
}

export default function VegasKarts() {
  const navigate = useNavigate();
  const location = useLocation();
  const modalOpen = useSelector(selectModalOpen);

  const initialPanel = pathToPanel(location.pathname);

  // ── FIX: Always show boot on every page load/refresh ──
  const [booted, setBooted] = useState(false);

  const [currentPanel, setCurrentPanel] = useState(initialPanel);
  const [savedCallsign, setSavedCallsign] = useState("");
  const [savedEmail, setSavedEmail] = useState("");
  const [broadcasts] = useState(SEED_BROADCASTS);
  const [drivers] = useState(SEED_DRIVERS);
  const [showPopup, setShowPopup] = useState(false);

  const containerRef = useRef(null);

  const isScrollingRef = useRef(false);
  const scrollTimerRef = useRef(null);

  const isProgrammaticRef = useRef(false);
  const programmaticTimer = useRef(null);
  const lastModeRef = useRef(null);

  const wheelAccumRef = useRef(0);
  const wheelResetTimer = useRef(null);

  const isDesktop = useCallback(() => window.innerWidth >= 992, []);

  const applyDesktopTransform = useCallback((index, animated) => {
    const container = containerRef.current;
    if (!container) return;
    const panels = container.querySelectorAll(".vk-panel");
    let offset = 0;
    for (let i = 0; i < index; i++) {
      offset += panels[i]?.offsetWidth || window.innerWidth;
    }
    container.style.transition = animated
      ? "transform 0.55s cubic-bezier(0.16,1,0.3,1)"
      : "none";
    container.style.transform = `translateX(-${offset}px)`;
  }, []);

  const applyMobileLayout = useCallback((index) => {
    const container = containerRef.current;
    if (!container) return;
    container.style.transition = "none";
    container.style.transform = "none";
    setTimeout(() => {
      const el = document.getElementById(PANEL_IDS[index]);
      if (el) el.scrollIntoView({ behavior: "instant", block: "start" });
    }, 30);
  }, []);

  const scrollToPanel = useCallback((index) => {
    const total = PANEL_IDS.length;
    index = Math.max(0, Math.min(index, total - 1));
    setCurrentPanel(index);
    const targetPath = PANEL_ROUTES[index];
    if (window.location.pathname !== targetPath) {
      navigate(targetPath, { replace: false });
    }
    if (isDesktop()) {
      applyDesktopTransform(index, true);
    } else {
      isProgrammaticRef.current = true;
      clearTimeout(programmaticTimer.current);
      const el = document.getElementById(PANEL_IDS[index]);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      programmaticTimer.current = setTimeout(() => {
        isProgrammaticRef.current = false;
      }, 700);
    }
  }, [isDesktop, applyDesktopTransform, navigate]);

  const handleNav = useCallback((panel) => {
    if (panel === 8) {
      setShowPopup(true);
    } else {
      scrollToPanel(panel);
    }
  }, [scrollToPanel]);

  // Mount
  useEffect(() => {
    if (isDesktop()) {
      applyDesktopTransform(initialPanel, false);
    } else {
      setTimeout(() => {
        const el = document.getElementById(PANEL_IDS[initialPanel]);
        if (el) el.scrollIntoView({ behavior: "instant", block: "start" });
      }, 50);
    }
    lastModeRef.current = isDesktop() ? "desktop" : "mobile";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Browser back/forward
  useEffect(() => {
    const panel = pathToPanel(location.pathname);
    if (panel !== currentPanel) {
      setCurrentPanel(panel);
      if (isDesktop()) {
        applyDesktopTransform(panel, true);
      } else {
        isProgrammaticRef.current = true;
        clearTimeout(programmaticTimer.current);
        const el = document.getElementById(PANEL_IDS[panel]);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        programmaticTimer.current = setTimeout(() => {
          isProgrammaticRef.current = false;
        }, 700);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Resize
  useEffect(() => {
    const onResize = () => {
      const nowMode = isDesktop() ? "desktop" : "mobile";
      const lastMode = lastModeRef.current;
      if (nowMode === "desktop") {
        applyDesktopTransform(currentPanel, false);
        requestAnimationFrame(() => {
          if (containerRef.current) {
            containerRef.current.style.transition =
              "transform 0.55s cubic-bezier(0.16,1,0.3,1)";
          }
        });
      } else {
        if (lastMode === "desktop") applyMobileLayout(currentPanel);
      }
      lastModeRef.current = nowMode;
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPanel, isDesktop, applyDesktopTransform, applyMobileLayout]);

  // Mobile scroll sync
  useEffect(() => {
    const onScroll = () => {
      if (isDesktop()) return;
      if (isProgrammaticRef.current) return;
      const threshold = window.innerHeight * 0.4;
      let activeIndex = 0;
      for (let i = 0; i < PANEL_IDS.length; i++) {
        const el = document.getElementById(PANEL_IDS[i]);
        if (!el) continue;
        if (el.getBoundingClientRect().top <= threshold) activeIndex = i;
      }
      if (currentPanel !== activeIndex) {
        setCurrentPanel(activeIndex);
        const targetPath = PANEL_ROUTES[activeIndex];
        if (window.location.pathname !== targetPath) {
          navigate(targetPath, { replace: true });
        }
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isDesktop, navigate, currentPanel]);

  // Wheel
  useEffect(() => {
    const onWheel = (e) => {
      if (!isDesktop()) return;
      e.preventDefault();
      if (isScrollingRef.current) return;
      const delta = e.deltaX !== 0 ? e.deltaX : e.deltaY;
      if (Math.abs(delta) < 5) return;
      isScrollingRef.current = true;
      setCurrentPanel(prev => {
        const next = delta > 0 ? Math.min(prev + 1, 8) : Math.max(prev - 1, 0);
        setTimeout(() => scrollToPanel(next), 0);
        return next;
      });
      clearTimeout(scrollTimerRef.current);
      scrollTimerRef.current = setTimeout(() => {
        isScrollingRef.current = false;
      }, 750);
    };
    window.addEventListener("wheel", onWheel, { passive: false });
    return () => window.removeEventListener("wheel", onWheel);
  }, [scrollToPanel, isDesktop]);

  // Keyboard
  useEffect(() => {
    const onKey = (e) => {
      if (!isDesktop()) return;
      if (e.key === "ArrowRight" || e.key === "ArrowDown")
        scrollToPanel(currentPanel + 1);
      else if (e.key === "ArrowLeft" || e.key === "ArrowUp")
        scrollToPanel(currentPanel - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [currentPanel, scrollToPanel, isDesktop]);

  // Touch swipe
  useEffect(() => {
    let tx = 0, ty = 0;
    const onTouchStart = (e) => {
      tx = e.touches[0].clientX;
      ty = e.touches[0].clientY;
    };
    const onTouchEnd = (e) => {
      if (!isDesktop()) return;
      const dx = tx - e.changedTouches[0].clientX;
      const dy = ty - e.changedTouches[0].clientY;
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 50) {
        scrollToPanel(currentPanel + (dx > 0 ? 1 : -1));
      }
    };
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchend", onTouchEnd);
    };
  }, [currentPanel, scrollToPanel, isDesktop]);

  const handleRegistered = (callsign, email) => {
    setSavedCallsign(callsign);
    setSavedEmail(email);
  };

  return (
    <div className="vk-root">
      {/* Boot shows on every page load — no sessionStorage gate */}
      {!booted && (
        <BootOverlay onDone={() => {
          setBooted(true);
        }} />
      )}

      <HudNav currentPanel={currentPanel} onNav={handleNav} />

      <div className="vk-scroll-track">
        <div className="vk-panels-container" ref={containerRef}>
          <PanelHero onNav={handleNav} />
          <PanelWorld onNav={handleNav} />
          <PanelKart onNav={handleNav} />
          <PanelTours onNav={handleNav} broadcasts={broadcasts} drivers={drivers} />
          <PanelDriver onNav={handleNav} onRegistered={handleRegistered} />
          <PanelMissions onNav={handleNav} />
          <PanelInfo onNav={handleNav} />
          <PanelCorporate onNav={handleNav} />
        </div>
      </div>

      {modalOpen && (
        <MissionModal
          savedCallsign={savedCallsign}
          savedEmail={savedEmail}
        />
      )}

      <AppPromoPopup
        onClose={() => setShowPopup(false)}
        delay={2800}
        forceOpen={showPopup}
      />

      <MobileBottomNav currentPanel={currentPanel} onNav={handleNav} />
    </div>
  );
}